import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { ViewIcon, EditIcon, DeleteIcon, AddIcon } from '../components/Icons';
import './AdminGiftCards.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminGiftCards() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // all, active, inactive
  });
  const [editingCard, setEditingCard] = useState(null);
  const [editForm, setEditForm] = useState({
    code: '',
    initial_balance: 0,
    recipient_email: '',
    recipient_name: '',
    message_lt: '',
    message_en: '',
    message_de: '',
    is_active: true,
    valid_from: '',
    valid_until: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const { data: cardsData, isLoading } = useQuery({
    queryKey: ['admin-gift-cards', page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active' ? '1' : '0');

      const res = await apiClient.get(`/admin/gift-cards?${params.toString()}`);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/gift-cards', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-gift-cards']);
      setShowCreateModal(false);
      setEditingCard(null);
      success('Dovanų kortelė sėkmingai sukurta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant dovanų kortelę');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/gift-cards/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-gift-cards']);
      setShowCreateModal(false);
      setEditingCard(null);
      success('Dovanų kortelė sėkmingai atnaujinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atnaujinant dovanų kortelę');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/gift-cards/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-gift-cards']);
      success('Dovanų kortelė pašalinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant dovanų kortelę');
    },
  });

  const handleEdit = (card) => {
    setEditingCard(card);
    setEditForm({
      code: card.code || '',
      initial_balance: parseFloat(card.initial_balance) || 0,
      recipient_email: card.recipient_email || '',
      recipient_name: card.recipient_name || '',
      message_lt: card.message_lt ?? card.message ?? '',
      message_en: card.message_en ?? '',
      message_de: card.message_de ?? '',
      is_active: card.is_active !== undefined ? card.is_active : true,
      valid_from: card.valid_from ? card.valid_from.split('T')[0] + 'T' + card.valid_from.split('T')[1]?.substring(0, 5) : '',
      valid_until: card.valid_until ? card.valid_until.split('T')[0] + 'T' + card.valid_until.split('T')[1]?.substring(0, 5) : ''
    });
    setShowCreateModal(true);
  };

  const handleNew = () => {
    setEditingCard(null);
    setEditForm({
      code: '',
      initial_balance: 0,
      recipient_email: '',
      recipient_name: '',
      message_lt: '',
      message_en: '',
      message_de: '',
      is_active: true,
      valid_from: '',
      valid_until: ''
    });
    setShowCreateModal(true);
  };

  const handleSave = () => {
    if (!editForm.initial_balance || parseFloat(editForm.initial_balance) <= 0) {
      showError('Nurodykite pradinį balansą');
      return;
    }

    const dataToSend = {
      ...editForm,
      code: editForm.code ? editForm.code.toUpperCase().trim() : undefined,
      valid_from: editForm.valid_from || null,
      valid_until: editForm.valid_until || null
    };

    if (editingCard) {
      updateMutation.mutate({ id: editingCard.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('lt-LT');
  };

  if (isLoading) {
    return <div className="admin-gift-cards-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-gift-cards-page container">
      <div className="gift-cards-header">
        <h1>Dovanų kortelės</h1>
        <button onClick={handleNew} className="btn btn-primary btn-sm">
          <AddIcon />
          Pridėti dovanų kortelę
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal kodą, el. paštą..."
            />
          </div>
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
              className="input"
            >
              <option value="all">Visi</option>
              <option value="active">Aktyvūs</option>
              <option value="inactive">Neaktyvūs</option>
            </select>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '', status: 'all' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      <div className="gift-cards-table-wrapper">
        <table className="gift-cards-table">
          <thead>
            <tr>
              <th>Kodas</th>
              <th>Balansas</th>
              <th>Gavėjas</th>
              <th>Galioja iki</th>
              <th>Statusas</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {cardsData?.gift_cards && cardsData.gift_cards.length > 0 ? (
              cardsData.gift_cards.map((card) => (
              <tr key={card.id}>
                <td>
                  <strong>{card.code}</strong>
                </td>
                <td>
                  {parseFloat(card.current_balance).toFixed(2)} € / {parseFloat(card.initial_balance).toFixed(2)} €
                </td>
                <td>
                  {card.recipient_name || card.recipient_email || '—'}
                </td>
                <td>{formatDate(card.valid_until)}</td>
                <td>
                  <span className={`status-badge ${card.is_active ? 'active' : 'inactive'}`}>
                    {card.is_active ? 'Aktyvus' : 'Neaktyvus'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => {
                        setSelectedCardId(card.id);
                        setShowUsageModal(true);
                      }}
                      className="btn-icon btn-icon-secondary"
                      title="Istorija"
                    >
                      <ViewIcon />
                    </button>
                    <button
                      onClick={() => handleEdit(card)}
                      className="btn-icon btn-icon-secondary"
                      title="Redaguoti"
                    >
                      <EditIcon />
                    </button>
                    <button
                      onClick={() => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Pašalinti dovanų kortelę',
                          message: `Ar tikrai norite pašalinti dovanų kortelę "${card.code}"?`,
                          type: 'warning',
                          confirmText: 'Pašalinti',
                          cancelText: 'Atšaukti',
                          onConfirm: () => {
                            deleteMutation.mutate(card.id);
                            setConfirmModal({ ...confirmModal, isOpen: false });
                          },
                          onCancel: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                        });
                      }}
                      className="btn-icon btn-icon-danger"
                      title="Pašalinti"
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  Dovanų kortelių nėra
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {cardsData?.pagination && (
        <Pagination
          page={cardsData.pagination.page}
          totalPages={cardsData.pagination.pages}
          totalItems={cardsData.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <GiftCardModal
          editForm={editForm}
          setEditForm={setEditForm}
          editingCard={editingCard}
          onSave={handleSave}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCard(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}

      {/* Naudojimų istorijos modalas */}
      {showUsageModal && (
        <GiftCardUsageModal
          cardId={selectedCardId}
          onClose={() => {
            setShowUsageModal(false);
            setSelectedCardId(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText || 'Patvirtinti'}
        cancelText={confirmModal.cancelText || 'Atšaukti'}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => {
          if (confirmModal.onCancel) {
            confirmModal.onCancel();
          }
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
      />
    </div>
  );
}

// Dovanų kortelės modalas
function GiftCardModal({ editForm, setEditForm, editingCard, onSave, onClose, isLoading }) {
  const [activeLang, setActiveLang] = useState('lt');
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{editingCard ? 'Redaguoti dovanų kortelę' : 'Nauja dovanų kortelė'}</h2>

        {!editingCard && (
          <div className="form-group">
            <label>Kodas (palikite tuščią, kad būtų sugeneruotas automatiškai)</label>
            <input
              type="text"
              value={editForm.code}
              onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
              className="input"
              placeholder="XXXX-XXXX-XXXX-XXXX"
            />
          </div>
        )}

        <div className="form-group">
          <label>Pradinis balansas (€) *</label>
          <input
            type="number"
            step="0.01"
            value={editForm.initial_balance}
            onChange={(e) => setEditForm({ ...editForm, initial_balance: parseFloat(e.target.value) || 0 })}
            className="input"
            required
            disabled={!!editingCard}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Gavėjo vardas</label>
            <input
              type="text"
              value={editForm.recipient_name}
              onChange={(e) => setEditForm({ ...editForm, recipient_name: e.target.value })}
              className="input"
            />
          </div>

          <div className="form-group">
            <label>Gavėjo el. paštas</label>
            <input
              type="email"
              value={editForm.recipient_email}
              onChange={(e) => setEditForm({ ...editForm, recipient_email: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <p className="admin-lang-hint">Žinutė gavėjui – galite įvesti trimis kalbomis (LT / EN / DE).</p>
        <div className="admin-lang-tabs">
          {LOCALES.map(({ code, label }) => (
            <button
              key={code}
              type="button"
              className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
              onClick={() => setActiveLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="form-group">
          <label>Žinutė ({LOCALES.find(l => l.code === activeLang)?.label})</label>
          <textarea
            value={editForm[`message_${activeLang}`] ?? ''}
            onChange={(e) => setEditForm({ ...editForm, [`message_${activeLang}`]: e.target.value })}
            className="input"
            rows="3"
            placeholder={activeLang === 'lt' ? 'Žinutė gavėjui' : activeLang === 'en' ? 'Message to recipient' : 'Nachricht an den Empfänger'}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Galioja nuo</label>
            <input
              type="datetime-local"
              value={editForm.valid_from}
              onChange={(e) => setEditForm({ ...editForm, valid_from: e.target.value })}
              className="input"
            />
          </div>

          <div className="form-group">
            <label>Galioja iki</label>
            <input
              type="datetime-local"
              value={editForm.valid_until}
              onChange={(e) => setEditForm({ ...editForm, valid_until: e.target.value })}
              className="input"
            />
          </div>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={editForm.is_active}
              onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
            />
            <span>Aktyvus</span>
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Atšaukti
          </button>
          <button
            onClick={onSave}
            disabled={isLoading || !editForm.initial_balance || parseFloat(editForm.initial_balance) <= 0}
            className="btn btn-primary"
          >
            {isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Naudojimų istorijos modalas
function GiftCardUsageModal({ cardId, onClose }) {
  const { data: usageData, isLoading } = useQuery({
    queryKey: ['gift-card-usage', cardId],
    queryFn: async () => {
      const res = await apiClient.get(`/admin/gift-cards/${cardId}/usage`);
      return res.data;
    },
    enabled: !!cardId,
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('lt-LT');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Dovanų kortelės naudojimų istorija</h2>

        {isLoading ? (
          <div>Kraunama...</div>
        ) : usageData?.usage && usageData.usage.length > 0 ? (
          <div className="usage-table-wrapper">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Užsakymas</th>
                  <th>Suma</th>
                </tr>
              </thead>
              <tbody>
                {usageData.usage.map((usage) => (
                  <tr key={usage.id}>
                    <td>{formatDate(usage.used_at)}</td>
                    <td>{usage.order_number || `#${usage.order_id}`}</td>
                    <td>{parseFloat(usage.amount).toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Naudojimų nėra</p>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Uždaryti
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminGiftCards;
